import type { PasurMove, PasurView } from "@bing/engine-pasur";
import { useState, type ReactNode } from "react";
import { fa } from "../i18n/fa";
import { PasurTable } from "../screens/PasurTable";
import { useSession } from "../store/context";
import type { SeatPlayer } from "./seat-identity";
import type { LobbyPlayer } from "./online-connection";
import { Boot } from "../components/Boot";
import { isCurrentPasurView, WinOverlay } from "./PasurGame";
import { OnlineGame } from "./OnlineGame";
import { finalDealOf } from "./pasur-breakdown";
import { PASUR_END_MARGIN_MS } from "./pasur-moments";
import { pasurOnlineRoom, pasurPrivateRoom, pasurSeatsOfJoin } from "./room-sizes";
import { getPasurPublicSeats } from "../settings/prefs";
import { END_REVEAL_DELAY_MS } from "./use-end-reveal";

/** The lobby roster as the felt and the result card both want it. ONE mapping, so a chair can
 *  never be a named person on the table and a nameless «بازیکن N» on the end screen. */
function seatPlayers(
  players: readonly LobbyPlayer[] | undefined,
): readonly SeatPlayer[] | undefined {
  return players?.map((p) => ({
    seat: p.seat,
    isBot: p.isBot,
    // The server resolves the display name; feeding it through `nickname` is what makes a seat
    // read app-consistently via `displayName()`.
    nickname: p.name,
    avatarId: p.user?.avatarId,
    avatarPhotoId: p.user?.avatarPhotoId,
    isOfficial: p.user?.isOfficial,
  }));
}

/** Online پاسور — thin wrapper over the shared `OnlineGame` shell, threading what حکم's and بی‌دل's
 *  do (Part G 45): the self profile, the roster, the turn timer, the host's bot-seat invite, the seat
 *  notices — and the chat toggle's footprint. */
export function PasurOnlineGame(): ReactNode {
  const profile = useSession((s) => s.profile);
  // THE TABLE'S SIZE IS ITS ROOM (operator, 2026-10-20): a public quick-match asks for the size the
  // lobby's tap chose (`getPasurPublicSeats`, a courier), a host's own table for the size picked on
  // «تنظیمات میز» (the create intent's `seats`; none — a friend invited from the list — is four).
  // ⚠️ `privateRoom` MUST BE STATED: `OnlineGame` would otherwise derive it from the PUBLIC name, so a
  // host whose last public tap was «۲ نفره» would get a table for two whatever they picked.
  const onlineJoin = useSession((s) => s.onlineJoin);
  // a moment on the felt, or one waiting: the end card waits for the table to drain (Task 8a)
  const [holding, setHolding] = useState(false);
  return (
    <OnlineGame<PasurMove, PasurView>
      gameId="pasur"
      room={pasurOnlineRoom(getPasurPublicSeats())}
      privateRoom={pasurPrivateRoom(pasurSeatsOfJoin(onlineJoin))}
      title={fa.pasur.title}
      // the end card waits for the table to DRAIN — the match's last deal end (its last capture, the
      // scoop), the deal's card and anything the terminal frame brought ahead of them (Task 8a,
      // residual 4: never a fixed time from the frame) — then PASUR_END_MARGIN_MS when the ENGINE
      // ended it; a match the room ends (a side gone, «opponents-left») leaves its frame mid-deal: no
      // deal end comes, and the shell's one beat runs from the drain (a moment still on screen shows
      // whole first — fix round 1, F2a)
      endRevealHeld={holding}
      endRevealMs={(v) => (v.phase === "gameOver" ? PASUR_END_MARGIN_MS : END_REVEAL_DELAY_MS)}
      renderTable={({ view, legal, send, leave, players, timer, onInviteSeat, seatNotices }) =>
        // a frame the table cannot draw — the OLD game-server's: the maintenance notice and the way
        // out, never a crash (final review A-2, I3 — `isCurrentPasurView`)
        !isCurrentPasurView(view) ? (
          <Boot error={fa.errors.MAINTENANCE} onLeave={leave} leaveLabel={fa.pasur.leave} />
        ) : (
          <PasurTable
            seatNotices={seatNotices}
            view={view}
            legal={legal}
            onMove={send}
            onLeave={leave}
            self={profile}
            onHolding={setHolding}
            timer={timer}
            onInviteSeat={onInviteSeat}
            // EVERY online table gets the chat toggle — OnlineGame mounts it iff `lobby?.roomId`, sent
            // on every lobby frame — so its footprint is kept from the FIRST frame (a resume's state
            // frame lands before its lobby frame; a slot that waited for it would move «پاسور»).
            chat
            players={seatPlayers(players)}
          />
        )
      }
      renderWin={({ result, view, again, leave, players, againDisabled, againHint, againHidden }) =>
        // the old game-server's frame has no match totals to show: the notice stands in its place
        !isCurrentPasurView(view) ? null : (
          <WinOverlay
            result={result}
            // The match totals come off the VIEW: a match the room ends (a side gone) carries a
            // 1/0 `seatScores`, which is no total at all.
            totalScores={view.totalScores}
            targetScore={view.targetScore}
            you={view.you}
            // The SAME identity pair the felt gets — without them the result card called every
            // real opponent «بازیکن N» and gave them a stand-in face.
            self={profile}
            players={seatPlayers(players)}
            onAgain={again}
            againDisabled={againDisabled}
            againHint={againHint}
            againHidden={againHidden}
            // the deal that ended the match — none when the room ended it mid-deal
            finalDeal={finalDealOf(view)}
            onLeave={leave}
          />
        )
      }
    />
  );
}
